/**
 * Endpoints + hooks TanStack Query pour les ressources patrimoniales
 * (immo, finance, prêts, fiscal, donations, will, prévoyance, biens
 * atypiques, objectifs).
 *
 * Toutes les ressources sont nested sous /client-files/{cf_id}/...
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { clientFileKeys } from "./client-files";
import { crmKeys } from "./crm";
import { historyKeys } from "./history";
import type {
  AtypicalAssetCreate,
  AtypicalAssetRead,
  AtypicalAssetUpdate,
  DonationCreate,
  DonationRead,
  DonationUpdate,
  FinancialAssetCreate,
  FinancialAssetRead,
  FinancialAssetUpdate,
  InsurancePolicyCreate,
  InsurancePolicyRead,
  InsurancePolicyUpdate,
  LoanCreate,
  LoanRead,
  LoanUpdate,
  ObjectiveCreate,
  ObjectiveRead,
  ObjectiveUpdate,
  RealEstateAssetCreate,
  RealEstateAssetRead,
  RealEstateAssetUpdate,
  TaxSituationRead,
  TaxSituationUpsert,
  WillRead,
  WillUpsert,
  PatrimonySummary,
} from "./schema";

// Préfixes
const base = (cf: string) => `/client-files/${cf}`;

export const patrimonySummaryKeys = {
  detail: (cf: string) => ["patrimony-summary", cf] as const,
};

/**
 * Tout ce qui se calcule à partir des lignes patrimoniales d'un dossier :
 * bilan consolidé (actif / passif / net / répartition), bilans « foyer » du
 * CRM (un dossier peut y contribuer) et historique des champs (provenance
 * affichée dans l'onglet Patrimoine). Sans cela, un montant corrigé sur place
 * laissait le bilan afficher l'ancien total.
 */
export function invalidatePatrimonyDerived(
  qc: ReturnType<typeof useQueryClient>,
  cf: string,
): void {
  void qc.invalidateQueries({ queryKey: patrimonySummaryKeys.detail(cf) });
  void qc.invalidateQueries({ queryKey: [...crmKeys.all, "household"] });
  void qc.invalidateQueries({ queryKey: historyKeys.raw(cf) });
}

// ---------------------------------------------------------------------------
// Helper factory pour les ressources nested CRUD list/create/update/delete
// ---------------------------------------------------------------------------

interface ResourceConfig {
  /** Segment d'URL après /client-files/{cf_id}/ — ex: "real-estate" */
  segment: string;
  /** Préfixe court pour query keys */
  key: string;
}

function makeResource<TRead, TCreate, TUpdate>(cfg: ResourceConfig) {
  const path = (cf: string, id?: string) => `${base(cf)}/${cfg.segment}${id ? `/${id}` : ""}`;

  const apiObj = {
    list: (cf: string) => api.get<TRead[]>(path(cf)),
    create: (cf: string, payload: TCreate) => api.post<TRead>(path(cf), payload),
    update: (cf: string, id: string, payload: TUpdate) => api.patch<TRead>(path(cf, id), payload),
    delete: (cf: string, id: string) => api.delete<void>(path(cf, id)),
  };

  const keys = {
    all: [cfg.key] as const,
    list: (cf: string) => [cfg.key, "list", cf] as const,
  };

  function useList(cf: string | undefined) {
    return useQuery({
      queryKey: cf ? keys.list(cf) : [cfg.key, "noop"],
      queryFn: () => apiObj.list(cf as string),
      enabled: !!cf,
    });
  }

  function invalidate(qc: ReturnType<typeof useQueryClient>, cf: string) {
    qc.invalidateQueries({ queryKey: keys.list(cf) });
    qc.invalidateQueries({ queryKey: clientFileKeys.detail(cf) });
    invalidatePatrimonyDerived(qc, cf);
  }

  function useCreate(cf: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (p: TCreate) => apiObj.create(cf, p),
      onSuccess: () => invalidate(qc, cf),
    });
  }

  function useUpdate(cf: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: TUpdate }) =>
        apiObj.update(cf, id, payload),
      onSuccess: () => invalidate(qc, cf),
    });
  }

  function useDelete(cf: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => apiObj.delete(cf, id),
      onSuccess: () => invalidate(qc, cf),
    });
  }

  return { api: apiObj, keys, useList, useCreate, useUpdate, useDelete };
}

// ---------------------------------------------------------------------------
// Resources standard (CRUD list)
// ---------------------------------------------------------------------------

export const realEstate = makeResource<
  RealEstateAssetRead,
  RealEstateAssetCreate,
  RealEstateAssetUpdate
>({ segment: "real-estate", key: "real-estate" });

export const financialAssets = makeResource<
  FinancialAssetRead,
  FinancialAssetCreate,
  FinancialAssetUpdate
>({ segment: "financial-assets", key: "financial-assets" });

export const loans = makeResource<LoanRead, LoanCreate, LoanUpdate>({
  segment: "loans",
  key: "loans",
});

export const donations = makeResource<DonationRead, DonationCreate, DonationUpdate>({
  segment: "donations",
  key: "donations",
});

export const insurancePolicies = makeResource<
  InsurancePolicyRead,
  InsurancePolicyCreate,
  InsurancePolicyUpdate
>({ segment: "insurance-policies", key: "insurance-policies" });

export const atypicalAssets = makeResource<
  AtypicalAssetRead,
  AtypicalAssetCreate,
  AtypicalAssetUpdate
>({ segment: "atypical-assets", key: "atypical-assets" });

export const objectives = makeResource<ObjectiveRead, ObjectiveCreate, ObjectiveUpdate>({
  segment: "objectives",
  key: "objectives",
});

// ---------------------------------------------------------------------------
// Resources singletons (1-1) — Tax + Will avec GET/PUT
// ---------------------------------------------------------------------------

export const taxSituationApi = {
  get: (cf: string) => api.get<TaxSituationRead>(`${base(cf)}/tax`),
  upsert: (cf: string, payload: TaxSituationUpsert) =>
    api.patch<TaxSituationRead>(`${base(cf)}/tax`, payload), // PATCH-style, l'API utilise PUT
};

export function useTaxSituation(cf: string | undefined) {
  return useQuery({
    queryKey: cf ? ["tax", cf] : ["tax", "noop"],
    queryFn: () => api.get<TaxSituationRead>(`${base(cf as string)}/tax`),
    enabled: !!cf,
  });
}

export function useUpsertTax(cf: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaxSituationUpsert) =>
      api.put<TaxSituationRead>(`${base(cf)}/tax`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tax", cf] }),
  });
}

export function useWill(cf: string | undefined) {
  return useQuery({
    queryKey: cf ? ["will", cf] : ["will", "noop"],
    queryFn: () => api.get<WillRead>(`${base(cf as string)}/will`),
    enabled: !!cf,
  });
}

export function useUpsertWill(cf: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WillUpsert) => api.put<WillRead>(`${base(cf)}/will`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["will", cf] }),
  });
}

// ---------------------------------------------------------------------------
// Bilan patrimonial consolidé (frontend v2) — lecture seule.
// Portée dossier.
// ---------------------------------------------------------------------------

export function usePatrimonySummary(cf: string | undefined) {
  return useQuery({
    queryKey: cf ? patrimonySummaryKeys.detail(cf) : ["patrimony-summary", "noop"],
    queryFn: () => api.get<PatrimonySummary>(`${base(cf as string)}/patrimony-summary`),
    enabled: !!cf,
  });
}
